import { addDays, formatISO, parseISO } from "date-fns";
import {
    BankReconciliation,
    ReconciliationUnmatchedBehavior,
    TransactionForReconciliation,
    TransactionForReconciliationFromAccount,
    TransactionForReconciliationFromStatement,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import { FinancialAccountType } from "@common/types/financialAccount";
import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import { addCalendarDays } from "@common/helpers/date";
import { canTakeStatementDate } from "@common/helpers/reconciliationCashItems";

interface IsDateOnlyInReconciledPeriodParams {
    dateOnly: string;
    previousClosingDate: string | null | undefined;
}

interface EarliestAllowedMatchDateOnlyParams {
    previousClosingDate?: string | null;
    lockDateOnly?: string | null;
}

/**
 * A bank transaction whose dateOnly falls on or before the previous
 * reconciliation's closing date is already in a reconciled period and
 * cannot clear a check in the current period.
 */
export function isDateOnlyInReconciledPeriod({
    dateOnly,
    previousClosingDate,
}: IsDateOnlyInReconciledPeriodParams): boolean {
    return previousClosingDate != null && dateOnly <= previousClosingDate;
}

interface IsDateOnlyOutsideStatementPeriodParams {
    dateOnly: string | null | undefined;
    openingDate: string;
    closingDate: string;
}

export function isDateOnlyOutsideStatementPeriod({
    dateOnly,
    openingDate,
    closingDate,
}: IsDateOnlyOutsideStatementPeriodParams): boolean {
    return dateOnly == null || dateOnly < openingDate || dateOnly > closingDate;
}

/**
 * First calendar day that may appear in check-match search results: the day
 * after the previous close and/or the day after the lock, whichever is later.
 */
export function earliestAllowedMatchDateOnly({
    previousClosingDate,
    lockDateOnly,
}: EarliestAllowedMatchDateOnlyParams): string | undefined {
    const floors: string[] = [];
    if (previousClosingDate) {
        floors.push(addCalendarDays(previousClosingDate, 1));
    }
    if (lockDateOnly) {
        floors.push(addCalendarDays(lockDateOnly, 1));
    }
    if (floors.length === 0) {
        return undefined;
    }
    return floors.reduce((latest, floor) => (floor > latest ? floor : latest));
}

export function getLastBankReconciliationForAccount<
    T extends Pick<BankReconciliation, "financialAccountId" | "closingDate">,
>(
    reconciliations: readonly T[] | undefined,
    financialAccountId: number | undefined,
): T | undefined {
    if (financialAccountId == null || !reconciliations) {
        return undefined;
    }
    return reconciliations
        .filter(
            (reconciliation) =>
                reconciliation.financialAccountId === financialAccountId,
        )
        .sort((left, right) =>
            left.closingDate.localeCompare(right.closingDate),
        )
        .at(-1);
}

export function getBookkeepingStartDate(
    financialAccount:
        | { bookkeepingStartDate: string | null }
        | null
        | undefined,
    entity: { bookkeepingStartDate: string },
): string {
    if (
        financialAccount?.bookkeepingStartDate &&
        financialAccount.bookkeepingStartDate > entity.bookkeepingStartDate
    ) {
        return financialAccount.bookkeepingStartDate;
    }

    return entity.bookkeepingStartDate;
}

export function getBookkeepingEndDate(
    financialAccount: { bookkeepingEndDate: string | null } | null | undefined,
    entity: { bookkeepingEndDate: string | null },
): string | null {
    if (
        financialAccount?.bookkeepingEndDate &&
        (!entity.bookkeepingEndDate ||
            financialAccount.bookkeepingEndDate < entity.bookkeepingEndDate)
    ) {
        return financialAccount.bookkeepingEndDate;
    }

    return entity.bookkeepingEndDate;
}

interface IsDateWithinBookkeepingRangeParams {
    dateOnly: string;
    financialAccount:
        | {
              bookkeepingStartDate: string | null;
              bookkeepingEndDate: string | null;
          }
        | null
        | undefined;
    entity: {
        bookkeepingStartDate: string;
        bookkeepingEndDate: string | null;
    };
}

export function isDateWithinBookkeepingRange({
    dateOnly,
    financialAccount,
    entity,
}: IsDateWithinBookkeepingRangeParams): boolean {
    const startDate = getBookkeepingStartDate(financialAccount, entity);
    if (dateOnly < startDate) {
        return false;
    }

    const bookkeepingEndDate = getBookkeepingEndDate(financialAccount, entity);
    if (!bookkeepingEndDate) {
        return true;
    }

    return dateOnly <= bookkeepingEndDate;
}

export function getReconciliationStartDate(
    previousReconciliationClosingDate: string | null,
    bookkeepingStartDate: string,
): string {
    if (previousReconciliationClosingDate) {
        return formatISO(
            addDays(parseISO(previousReconciliationClosingDate), 1),
            { representation: "date" },
        );
    }

    return bookkeepingStartDate;
}

interface DisplayedKickBalances {
    statementsCashIn: number;
    statementsCashOut: number;
    kickReconciledCashIn: number;
    kickReconciledCashOut: number;
    kickUnreconciled: number;
    kickFromPreviousPeriod: number;
    kickOpeningBalanceAsOfReconciliationStart: number;
    kickClosingReconciledBalance: number;
    kickClosingUnreconciledBalance: number;
}

interface PendingCheckMergeForBalanceBase {
    checkTransactionId: number;
    amount: number;
    date: string;
}

export type PendingCheckMergeForBalance = PendingCheckMergeForBalanceBase &
    ({ bankTransactionId: number } | { statementTransactionId: string });

interface ComputeDisplayedKickBalancesParams {
    transactionsPairs: TransactionsPairForReconciliation[];
    kickOpeningDate: string;
    kickOpeningBalance: number | null;
    kickOpeningBalanceDifference: number;
    statementsOpeningDate: string;
    statementsClosingDate: string;
    financialTransactionsToRemoveIds: number[];
    statementTransactionsToAddIds: string[];
    financialTransactionIdsIgnoringDateChanges: number[];
    financialTransactionsFromPreviousPeriodIds: number[];
    isLiability: boolean;
    checkMerges?: PendingCheckMergeForBalance[];
}

export const FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION: FinancialAccountType[] =
    [
        FinancialAccountType.CHECKING,
        FinancialAccountType.SAVINGS,
        FinancialAccountType.CREDIT_CARD,
    ];

export function getStatmentTransactionId(
    statementTransaction: Pick<
        TransactionForReconciliationFromStatement,
        "financialDocumentId" | "row"
    >,
): string {
    return `${statementTransaction.financialDocumentId}-${statementTransaction.row}`;
}

export function pairHasDifference(
    pair: TransactionsPairForReconciliation,
): boolean {
    if (
        pair.fromAccount &&
        !pair.fromStatement &&
        pair.fromAccount.unmatchedBehavior ===
            ReconciliationUnmatchedBehavior.IGNORE
    ) {
        return false;
    }

    return !pair.fromAccount || !pair.fromStatement;
}

export function determineFinancialTransactionDateAfterReconciliation(
    transactionFromAccount: TransactionForReconciliationFromAccount,
    transactionFromStatement:
        | TransactionForReconciliationFromStatement
        | undefined,
    financialTransactionIdsIgnoringDateChanges: number[],
): string {
    if (
        !transactionFromStatement ||
        financialTransactionIdsIgnoringDateChanges.includes(
            transactionFromAccount.financialTransactionId,
        ) ||
        !canTakeStatementDate(transactionFromAccount, transactionFromStatement)
    ) {
        return transactionFromAccount.date;
    }
    return transactionFromStatement.date;
}

/**
 * Computes displayed Kick balances from reconciliation summary and user
 * overrides (removals, adds, ignore-date-changes, from-previous-period).
 */
export function computeDisplayedKickBalances(
    params: ComputeDisplayedKickBalancesParams,
): DisplayedKickBalances {
    const {
        transactionsPairs,
        kickOpeningDate,
        kickOpeningBalance,
        kickOpeningBalanceDifference,
        statementsOpeningDate,
        statementsClosingDate,
        financialTransactionsToRemoveIds,
        statementTransactionsToAddIds,
        financialTransactionIdsIgnoringDateChanges,
        financialTransactionsFromPreviousPeriodIds,
        isLiability,
        checkMerges = [],
    } = params;
    const absorbedBankTransactionIds = new Set(
        checkMerges.flatMap((merge) =>
            "bankTransactionId" in merge ? [merge.bankTransactionId] : [],
        ),
    );
    const absorbedStatementTransactionIds = new Set(
        checkMerges.flatMap((merge) =>
            "statementTransactionId" in merge
                ? [merge.statementTransactionId]
                : [],
        ),
    );
    const mergedCheckTransactionIds = new Set(
        checkMerges.map((merge) => merge.checkTransactionId),
    );

    const reduced = transactionsPairs.reduce(
        (acc, pair) => {
            let kickTransactionToInclude:
                | TransactionForReconciliation
                | undefined;
            const transactionMarkedForRemoval =
                !!pair.fromAccount &&
                financialTransactionsToRemoveIds.includes(
                    pair.fromAccount.financialTransactionId,
                );
            const bankTransactionWasAbsorbed =
                (!!pair.fromAccount &&
                    absorbedBankTransactionIds.has(
                        pair.fromAccount.financialTransactionId,
                    )) ||
                (!!pair.fromStatement &&
                    absorbedStatementTransactionIds.has(
                        getStatmentTransactionId(pair.fromStatement),
                    ));

            if (pair.fromAccount && !transactionMarkedForRemoval) {
                const transactionFinalDate =
                    determineFinancialTransactionDateAfterReconciliation(
                        pair.fromAccount,
                        pair.fromStatement,
                        financialTransactionIdsIgnoringDateChanges,
                    );
                const transactionIsWithinStatementsDates =
                    transactionFinalDate >= statementsOpeningDate &&
                    transactionFinalDate <= statementsClosingDate;
                const matchedTransactionIsNotAfterStatement =
                    !!pair.fromStatement &&
                    transactionFinalDate <= statementsClosingDate;
                if (
                    matchedTransactionIsNotAfterStatement ||
                    transactionIsWithinStatementsDates
                ) {
                    kickTransactionToInclude = pair.fromAccount;
                }

                const wasCountedAsFromPreviousPeriod =
                    (!pair.fromAccount.isCashItem &&
                        pair.fromAccount.date < statementsOpeningDate &&
                        pair.fromAccount.date >= kickOpeningDate) ||
                    financialTransactionsFromPreviousPeriodIds.includes(
                        pair.fromAccount.financialTransactionId,
                    );
                const shouldMoveFromOpeningToReconciledCash =
                    !!pair.fromStatement &&
                    !!kickTransactionToInclude &&
                    !bankTransactionWasAbsorbed &&
                    wasCountedAsFromPreviousPeriod;
                if (shouldMoveFromOpeningToReconciledCash) {
                    acc.kickFromPreviousPeriod -= fromMonetaryToInt(
                        pair.fromAccount.amount,
                    );
                }
            }

            const transactionMarkedToAdd =
                pair.fromStatement &&
                statementTransactionsToAddIds.includes(
                    getStatmentTransactionId(pair.fromStatement),
                );
            if (transactionMarkedToAdd && pair.fromStatement) {
                kickTransactionToInclude = pair.fromStatement;
            }

            if (
                pair.fromStatement &&
                kickTransactionToInclude &&
                !bankTransactionWasAbsorbed
            ) {
                if (kickTransactionToInclude.amount > 0) {
                    acc.kickReconciledCashIn += fromMonetaryToInt(
                        kickTransactionToInclude.amount,
                    );
                } else if (kickTransactionToInclude.amount < 0) {
                    acc.kickReconciledCashOut += Math.abs(
                        fromMonetaryToInt(kickTransactionToInclude.amount),
                    );
                }
            }

            if (
                !pair.fromStatement &&
                kickTransactionToInclude &&
                pair.fromAccount
            ) {
                const isFromPreviousPeriod =
                    financialTransactionsFromPreviousPeriodIds.includes(
                        pair.fromAccount.financialTransactionId,
                    );
                const isClearedByMerge = mergedCheckTransactionIds.has(
                    pair.fromAccount.financialTransactionId,
                );
                if (isFromPreviousPeriod) {
                    acc.kickFromPreviousPeriod += fromMonetaryToInt(
                        kickTransactionToInclude.amount,
                    );
                } else if (!isClearedByMerge) {
                    acc.kickUnreconciled += fromMonetaryToInt(
                        kickTransactionToInclude.amount,
                    );
                }
            }

            if (pair.fromStatement) {
                if (pair.fromStatement.amount > 0) {
                    acc.statementsCashIn += fromMonetaryToInt(
                        pair.fromStatement.amount,
                    );
                } else if (pair.fromStatement.amount < 0) {
                    acc.statementsCashOut += Math.abs(
                        fromMonetaryToInt(pair.fromStatement.amount),
                    );
                }
            }

            return acc;
        },
        {
            statementsCashIn: 0,
            statementsCashOut: 0,
            kickReconciledCashIn: 0,
            kickReconciledCashOut: 0,
            kickUnreconciled: 0,
            kickFromPreviousPeriod: 0,
        },
    );

    for (const merge of checkMerges) {
        const amountInt = fromMonetaryToInt(merge.amount);
        if (amountInt > 0) {
            reduced.kickReconciledCashIn += amountInt;
        } else if (amountInt < 0) {
            reduced.kickReconciledCashOut += Math.abs(amountInt);
        }
    }

    const kickFromPreviousPeriodAdjusted = isLiability
        ? -reduced.kickFromPreviousPeriod
        : reduced.kickFromPreviousPeriod;
    const kickOpeningBalanceAsOfReconciliationStart =
        fromMonetaryToInt(kickOpeningBalance ?? 0) +
        fromMonetaryToInt(kickOpeningBalanceDifference) +
        kickFromPreviousPeriodAdjusted;
    const kickClosingReconciledBalance = isLiability
        ? kickOpeningBalanceAsOfReconciliationStart -
          reduced.kickReconciledCashIn +
          reduced.kickReconciledCashOut
        : kickOpeningBalanceAsOfReconciliationStart +
          reduced.kickReconciledCashIn -
          reduced.kickReconciledCashOut;
    const kickClosingUnreconciledBalance = isLiability
        ? kickClosingReconciledBalance - reduced.kickUnreconciled
        : kickClosingReconciledBalance + reduced.kickUnreconciled;

    return {
        statementsCashIn: fromIntToMonetary(reduced.statementsCashIn),
        statementsCashOut: fromIntToMonetary(reduced.statementsCashOut),
        kickReconciledCashIn: fromIntToMonetary(reduced.kickReconciledCashIn),
        kickReconciledCashOut: fromIntToMonetary(reduced.kickReconciledCashOut),
        kickUnreconciled: fromIntToMonetary(reduced.kickUnreconciled),
        kickFromPreviousPeriod: fromIntToMonetary(
            reduced.kickFromPreviousPeriod,
        ),
        kickOpeningBalanceAsOfReconciliationStart: fromIntToMonetary(
            kickOpeningBalanceAsOfReconciliationStart,
        ),
        kickClosingReconciledBalance: fromIntToMonetary(
            kickClosingReconciledBalance,
        ),
        kickClosingUnreconciledBalance: fromIntToMonetary(
            kickClosingUnreconciledBalance,
        ),
    };
}
